// A whole-document confirmation the engine refused, in words for a person (2026-10-09). The engine's
// own sentence reached the review screen's banner as it was: "The authoritative page images could not
// be sealed for confirmation." on every Confirm of a Linux host (CapturePoint's page pin had no Linux
// path). A refusal of a confirmation now says what happened to the person's work and what to do, and
// is reported to the product stream by kind, so a run of them shows on the Friction screen instead of
// waiting for somebody to mention it.
import { CORRECTION } from '../shared/desktop-api';
import { EngineRefusedError } from './engine-refused';
import { isTransient } from './engine-unreachable';
import { trackProduct } from './product-tracker';

/** What the banner says when a confirmation could not be saved; its Try again confirms again. */
export const CONFIRM_SAVE_FAILED =
  "Couldn't save this confirmation. Your work is kept — try again.";

/** The product event for one refused confirmation (docs/runbooks/product-analytics.md). */
export const CONFIRM_FAILED_EVENT = 'review.confirm_failed';

/**
 * The engine's own refusals a person can act on, kept in its words: what is still to do on the document
 * before it can be confirmed (CapturePoint's SealWholeDocumentConfirmation checks). Try again would not help.
 */
const actionable = [
  /^(Choose|Confirm|Fill|Resolve) .* before confirming the document\.$/i,
  /^Confirm every visible line item with the document\.$/i,
  /^A page that was not read cannot be confirmed/i,
];

interface Correction {
  kind?: unknown;
}

interface Entry {
  corrections?: Correction[];
}

/** Whether a save request carries a whole-document confirmation (the bulk save's entries, or one correction). */
export function confirmsDocument(request: {
  correctionEntries?: Entry[];
  correction?: Correction;
}): boolean {
  const corrections = [
    ...(request.correctionEntries ?? []).flatMap((entry) => entry.corrections ?? []),
    ...(request.correction ? [request.correction] : []),
  ];
  return corrections.some((correction) => correction.kind === CORRECTION.documentConfirmed);
}

/** The kind of a refusal, for the product stream: never the engine's words. */
function causeOf(error: EngineRefusedError): string {
  return /could not be sealed|pages couldn't be checked/i.test(error.message)
    ? 'pages-unsealed'
    : 'refused';
}

/**
 * The save, with a refused confirmation reworded and reported. Anything else passes through as it was:
 * a write over a newer version (ReviewConflictError), the engine restarting (it waits), Paige's
 * own refusals, and a save that confirms nothing.
 */
export function guardConfirmSave<Request extends object, Result>(
  save: (request: Request) => Promise<Result>,
): (request: Request) => Promise<Result> {
  return async (request) => {
    try {
      return await save(request);
    } catch (error) {
      if (!confirmsDocument(request)) throw error;
      // No answer (the connection dropped, or none came within its limit, engine-answer-limit.ts): whether it
      // landed is not known, so the person is told their work is kept and Try again asks again; the review
      // version's fence answers a second save of the same confirmation as a conflict, never twice (2026-10-10).
      if (isTransient(error)) {
        trackProduct(CONFIRM_FAILED_EVENT, { cause: 'no-answer' });
        throw new Error(CONFIRM_SAVE_FAILED, { cause: error });
      }
      if (!(error instanceof EngineRefusedError)) throw error;
      if (actionable.some((pattern) => pattern.test(error.message))) throw error;
      trackProduct(CONFIRM_FAILED_EVENT, { cause: causeOf(error) });
      throw new Error(CONFIRM_SAVE_FAILED, { cause: error });
    }
  };
}
